import {
  quantityFromSiteUnits,
  siteUnits,
} from '@calculations/utilities/selling-uom';
import { PriceStrategy } from '@snap-types/models/enums';
import type { PriceStrategyConfigWithRelations } from '@snap-types/models/price-strategy-config';
import { SnapSuggestionStatus } from '@snap-types/models/sort-status';
import { dec } from '@snap-types/types/decimal';
import { QuoteBuilderStateType } from '@ui/features/quote-builder/types/state';
import { QuoteLineType } from '@ui/features/quote-builder/types/state/quote-line';
import { QuotePriceType } from '@ui/features/quote-builder/types/state/quote-price';
import { v4 as uuid } from 'uuid';

import { buildIssueId } from '../../utilities/issue-id';
import { upsertIssue } from '../../utilities/issue-managers';
import { OnQuotePriceCreationInput } from '../types';
import { handleQuotePriceCreationFallback } from '../utilities/handle-quote-price-creation-fallback';

export function onPartQuotePriceCreation(
  state: QuoteBuilderStateType,
  quoteLine: QuoteLineType,
  priceStrategyConfigs: PriceStrategyConfigWithRelations[],
) {
  const previousQuotePriceId =
    quoteLine.quotePriceUuids[quoteLine.quotePriceUuids.length - 1];

  const previousQuotePrice = quoteLine.quotePrices[previousQuotePriceId];

  const id = uuid();

  /**
   * A new break starts one selling unit above the last one, which is what the
   * bump reads as in the UI. State is in eaches, so on a thousands site that is
   * a step of 1,000 — stepping by a literal 1 there would leave the user
   * looking at a quantity of 25.001.
   *
   * A service charge quantity counts services rather than pieces and always
   * steps by one, the same as everywhere else it is deliberately left in its
   * own basis.
   */
  const step = quoteLine.serviceChargeId
    ? dec(1)
    : quantityFromSiteUnits(siteUnits(dec(1)), {
        sellingUnitOfMeasure: state.sellingUnitOfMeasure,
      });

  const quantity = previousQuotePrice.quantity.plus(step);

  const newQuotePrice = {
    ...previousQuotePrice,
    uuid: id,
    status: 'creating',
    quantity,
    automatedQuantity: null,
    automatedBaseGrossMargin: null,
    automatedContributionMargin: null,
    automatedGrossMargin: null,
    automatedUnitPrice: null,
  } satisfies QuotePriceType;

  const { priceStrategy } = previousQuotePrice;

  const inputs = {
    state,
    quoteLine,
    quotePrice: newQuotePrice,
    priceStrategyConfigs,
  } satisfies OnQuotePriceCreationInput;

  const result = handleQuotePriceCreationFallback({
    currentStrategy: priceStrategy,
    priceStrategyConfigs,
    inputs,
  });

  const calculationIssueId = buildIssueId({
    area: 'quoteLine',
    quoteLineUuid: quoteLine.uuid,
    type: 'invalid',
  });

  if (result.status === 'failure') {
    // @TODO(NT): Our store should be integrated with toast notifications
    console.error('Failed to calculate price', result.reason);

    upsertIssue(state, calculationIssueId, {
      title: 'Failed to calculate price',
      description: 'Could not calculate price for new quote price',
      severity: 'warning',
      type: 'invalid',
      area: 'quoteLine',
      quoteLineUuid: quoteLine.uuid,
      action: {
        type: 'display',
      },
    });
    return;
  }

  if (result.status === 'no-op') {
    // No-ops still need to update the margins

    newQuotePrice.grossMargin =
      (newQuotePrice.grossMargin || result.grossMargin) ?? null;
    newQuotePrice.contributionMargin =
      (newQuotePrice.contributionMargin || result.contributionMargin) ?? null;

    return newQuotePrice;
  }

  // Update quotePrice.priceStrategy if fallback succeeded
  if (
    result.status === 'success' &&
    result.strategy &&
    result.strategy !== priceStrategy
  ) {
    result.quotePrice.priceStrategy = result.strategy;
  }

  if (result.status === 'fallback-failure') {
    upsertIssue(
      state,
      buildIssueId({
        area: 'quotePrice',
        quoteLineUuid: quoteLine.uuid,
        quotePriceUuid: result.quotePrice.uuid,
        type: 'invalid',
        field: 'quantity',
      }),
      {
        title: 'Could not calculate price for new quote price',
        description: result.reason,
        severity: 'warning',
        type: 'invalid',
        area: 'quoteLine',
        quoteLineUuid: quoteLine.uuid,
        action: {
          type: 'display',
        },
      },
    );
  }

  result.quotePrice.calculatedUnitPrice = result.quotePrice.unitPrice;

  const usingDefaultGrossMargin =
    previousQuotePrice.grossMargin &&
    previousQuotePrice.automatedGrossMargin &&
    previousQuotePrice.grossMargin.eq(previousQuotePrice.automatedGrossMargin);

  const usingDefaultBaseGrossMargin =
    previousQuotePrice.baseGrossMargin &&
    previousQuotePrice.automatedBaseGrossMargin &&
    previousQuotePrice.baseGrossMargin.eq(
      previousQuotePrice.automatedBaseGrossMargin,
    );

  const usingDefaultContributionMargin =
    previousQuotePrice.contributionMargin &&
    previousQuotePrice.automatedContributionMargin &&
    previousQuotePrice.contributionMargin.eq(
      previousQuotePrice.automatedContributionMargin,
    );

  // Use the updated strategy (may have changed due to fallback)
  const finalStrategy = result.quotePrice.priceStrategy;

  switch (finalStrategy) {
    case PriceStrategy.PriceSegmentation:
    case PriceStrategy.PriceSegmentationWithTargetGrossMargin:
    case PriceStrategy.CAPPriceSegmentation:
      // On the Price Segmentation strategies, the new line re-uses the existing line's base gross margin
      // If that value matches the default, then the value was the calculated value, and the user has
      // accepted the price
      if (usingDefaultBaseGrossMargin) {
        result.quotePrice.calculatedBaseGrossMargin =
          result.quotePrice.baseGrossMargin;
        result.quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
      } else {
        result.quotePrice.calculatedBaseGrossMargin = null;
      }
      break;

    case PriceStrategy.TargetContributionMargin:
      // On the Target Contribution Margin strategy, the new line re-uses the existing line's contribution margin;
      // If that value matches the default, then the value was the calculated value, and the user has
      // accepted the price
      if (usingDefaultContributionMargin) {
        result.quotePrice.calculatedContributionMargin =
          result.quotePrice.contributionMargin;
        result.quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
      } else {
        result.quotePrice.calculatedContributionMargin = null;
      }
      break;

    case PriceStrategy.TargetGrossMargin:
    case PriceStrategy.RAFTargetGrossMargin:
      // On the Target Gross Margin strategy, the new line re-uses the existing line's gross margin;
      // If that value matches the default, then the value was the calculated value, and the user has
      // accepted the price
      if (usingDefaultGrossMargin) {
        result.quotePrice.calculatedGrossMargin = result.quotePrice.grossMargin;
        result.quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
      } else {
        result.quotePrice.calculatedGrossMargin = null;
      }
      break;

    default:
      // On any other strategy, if the suggested price status is not explicitly excluded (as it is for UserPrice,
      // the New Part stratgies, etc.)), then the price is accepted
      if (
        result.quotePrice.suggestedPriceStatus != SnapSuggestionStatus.EXCLUDED
      ) {
        result.quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
      }
  }

  // Given that we are adding a quote price, by definition this is not an automatable quote line
  quoteLine.automatable = false;

  return result.quotePrice;
}
